import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, Trash2 } from 'lucide-react'
import { useId, useState } from 'react'
import { api, type ProjectBudgetPeriod, type ProjectBudgetPeriodInput } from '../../lib/api'
import { formatHours, validateBudgetPeriod } from '../../lib/budget-view'
import { useIsMobile } from '../../lib/use-media-query'
import { cn } from '../../lib/utils'
import { Button } from '../ui/button'
import { confirm } from '../ui/confirm-dialog'
import { Dialog } from '../ui/dialog'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { SidePanel } from '../ui/side-panel'

const EMPTY = { fromDate: '', toDate: '', hours: '', note: '' }

function errorText(error: Error) {
  if ('code' in error && error.code === 'BUDGET_PERIOD_OVERLAP')
    return 'Überschneidet sich mit einem bestehenden Zeitraum'
  if ('status' in error && (error.status === 403 || error.status === 404))
    return 'Zeiträume nicht verfügbar oder keine Berechtigung.'
  return error.message
}

export function BudgetPeriodsDialog({
  projectId,
  projectName,
  onClose,
}: {
  projectId: string
  projectName: string
  onClose: () => void
}) {
  const qc = useQueryClient()
  const mobile = useIsMobile()
  const id = useId()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState(EMPTY)
  const [error, setError] = useState<string | null>(null)
  const periods = useQuery({
    queryKey: ['project-budget-periods', projectId],
    queryFn: () => api.listBudgetPeriods(projectId),
    retry: false,
  })
  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['project-budget-periods', projectId] }),
      qc.invalidateQueries({ queryKey: ['project-budget-status', projectId] }),
    ])
  }
  const save = useMutation({
    mutationFn: (input: ProjectBudgetPeriodInput) =>
      editingId
        ? api.updateBudgetPeriod(projectId, editingId, input)
        : api.createBudgetPeriod(projectId, input),
    onSuccess: async () => {
      setForm(EMPTY)
      setEditingId(null)
      setError(null)
      await refresh()
    },
    onError: (err: Error) => setError(errorText(err)),
  })
  const remove = useMutation({
    mutationFn: (periodId: string) => api.deleteBudgetPeriod(projectId, periodId),
    onSuccess: async (_, periodId) => {
      if (editingId === periodId) {
        setEditingId(null)
        setForm(EMPTY)
      }
      setError(null)
      await refresh()
    },
    onError: (err: Error) => setError(errorText(err)),
  })
  const busy = save.isPending || remove.isPending
  const edit = (period: ProjectBudgetPeriod) => {
    setEditingId(period.id)
    setForm({
      fromDate: period.fromDate,
      toDate: period.toDate,
      hours: String(period.hours),
      note: period.note ?? '',
    })
    setError(null)
  }
  const Frame = mobile ? SidePanel : Dialog
  return (
    <Frame
      open
      onClose={onClose}
      title="Zeiträume pflegen"
      description={projectName}
      className={mobile ? undefined : 'sm:max-w-2xl'}
      footer={
        <Button variant="secondary" onClick={onClose}>
          Schließen
        </Button>
      }
    >
      <div className="space-y-4 pb-3">
        {periods.isLoading ? (
          <p className="text-sm text-text-tertiary">Lade Zeiträume …</p>
        ) : periods.error ? (
          <p role="alert" className="text-sm text-danger">
            {errorText(periods.error)}
          </p>
        ) : (
          <>
            {!mobile && (
              <div className="grid grid-cols-[1fr_1fr_0.7fr_1.4fr_80px] gap-2 text-[11px] uppercase text-text-tertiary">
                <span>Von</span>
                <span>Bis</span>
                <span>Stunden</span>
                <span>Notiz</span>
                <span />
              </div>
            )}
            <ul className="divide-y divide-border-subtle">
              {periods.data?.periods.map((period) => (
                <li
                  key={period.id}
                  className={cn(
                    'items-center gap-2 py-3',
                    mobile ? 'flex' : 'grid grid-cols-[1fr_1fr_0.7fr_1.4fr_80px]',
                  )}
                >
                  {mobile ? (
                    <div className="min-w-0 flex-1">
                      <p className="text-sm tabular-nums">
                        {new Date(`${period.fromDate}T12:00:00`).toLocaleDateString('de-DE')} –{' '}
                        {new Date(`${period.toDate}T12:00:00`).toLocaleDateString('de-DE')}
                      </p>
                      <p className="text-sm font-medium">{formatHours(period.hours)}</p>
                      {period.note && (
                        <p className="text-xs text-text-secondary break-words">{period.note}</p>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-xs tabular-nums">
                        {new Date(`${period.fromDate}T12:00:00`).toLocaleDateString('de-DE')}
                      </p>
                      <p className="text-xs tabular-nums">
                        {new Date(`${period.toDate}T12:00:00`).toLocaleDateString('de-DE')}
                      </p>
                      <p className="text-sm tabular-nums">{formatHours(period.hours)}</p>
                      <p className="text-xs text-text-secondary break-words">
                        {period.note || '—'}
                      </p>
                    </>
                  )}
                  <div className="flex shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={busy}
                      aria-label={`Zeitraum ab ${period.fromDate} bearbeiten`}
                      onClick={() => edit(period)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      disabled={busy}
                      aria-label={`Zeitraum ab ${period.fromDate} löschen`}
                      onClick={async () => {
                        if (
                          await confirm({
                            title: 'Zeitraum löschen?',
                            description:
                              'Das Kontingent wird entfernt. Gebuchte Zeiten bleiben erhalten.',
                            confirmLabel: 'Löschen',
                            variant: 'danger',
                          })
                        )
                          remove.mutate(period.id)
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
            {periods.data?.periods.length === 0 && (
              <p className="text-sm text-text-tertiary">Noch keine Zeiträume angelegt.</p>
            )}
            <form
              className="space-y-3"
              noValidate
              onSubmit={(event) => {
                event.preventDefault()
                if (busy) return
                const input = {
                  fromDate: form.fromDate,
                  toDate: form.toDate,
                  hours: Number(form.hours),
                  note: form.note.trim() || null,
                }
                const invalid = validateBudgetPeriod(input)
                setError(invalid)
                if (!invalid) save.mutate(input)
              }}
            >
              <h3 className="text-sm font-medium">
                {editingId ? 'Zeitraum bearbeiten' : 'Neuer Zeitraum'}
              </h3>
              <fieldset
                disabled={busy}
                className={cn(
                  'grid gap-3 min-w-0',
                  mobile ? 'grid-cols-2' : 'grid-cols-[1fr_1fr_0.7fr_1.4fr]',
                )}
              >
                <div className="min-w-0 space-y-1">
                  <Label htmlFor={`${id}-from`}>Von</Label>
                  <Input
                    id={`${id}-from`}
                    type="date"
                    value={form.fromDate}
                    onChange={(e) => setForm({ ...form, fromDate: e.target.value })}
                    required
                    // Desktop only, like the entry form: no keyboard pop-up on the phone sheet.
                    autoFocus={!mobile}
                    className="min-w-0 px-2"
                  />
                </div>
                <div className="min-w-0 space-y-1">
                  <Label htmlFor={`${id}-to`}>Bis</Label>
                  <Input
                    id={`${id}-to`}
                    type="date"
                    value={form.toDate}
                    onChange={(e) => setForm({ ...form, toDate: e.target.value })}
                    required
                    className="min-w-0 px-2"
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${id}-hours`}>Stunden</Label>
                  <Input
                    id={`${id}-hours`}
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={form.hours}
                    onChange={(e) => setForm({ ...form, hours: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor={`${id}-note`}>Notiz (optional)</Label>
                  <Input
                    id={`${id}-note`}
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    maxLength={2000}
                  />
                </div>
              </fieldset>
              {error && (
                <p role="alert" className="text-sm text-danger">
                  {error}
                </p>
              )}
              <p className="text-xs text-text-tertiary">
                Zeiträume dürfen sich nicht überschneiden. Lücken sind erlaubt.
              </p>
              <div className="flex justify-end gap-2">
                {editingId && (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => {
                      setEditingId(null)
                      setForm(EMPTY)
                      setError(null)
                    }}
                  >
                    Abbrechen
                  </Button>
                )}
                <Button type="submit" disabled={busy}>
                  {!editingId && <Plus className="h-4 w-4 mr-1" />}
                  {busy ? 'Speichert …' : editingId ? 'Speichern' : 'Zeitraum hinzufügen'}
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </Frame>
  )
}
